import api from "./api";

// Branch Management
const createBranch = async (branchData) => {
  const response = await api.post(`/admin-branches`, branchData);
  return response.data;
};

const getBranches = async (queryParams) => {
  const response = await api.get(`/admin-branches`, { params: queryParams });
  return response.data;
};

const getBranch = async (branchId) => {
  const response = await api.get(`/admin-branches/${branchId}`);
  return response.data;
};

const getBranchByCode = async (branchCode) => {
  const response = await api.get(`/branches/code/${branchCode}`);
  return response.data?.branch;
};

const updateBranch = async (branchId, branchData) => {
  const response = await api.put(`/admin-branches/${branchId}`, branchData);
  return response.data;
};

const configureRateMode = async (branchId, rateConfig) => {
  const response = await api.put(
    `/admin-branches/${branchId}/rate-config`,
    rateConfig
  );
  return response.data;
};

const setMainBranch = async (branchId) => {
  const response = await api.post(`/admin-branches/set-main`, { branchId });
  return response.data;
};

const copyConfiguration = async (sourceBranchId, targetBranchId) => {
  const response = await api.post(`/admin-branches/copy-config`, {
    sourceBranchId,
    targetBranchId,
  });
  return response.data;
};

const deactivateBranch = async (branchId, reason) => {
  const response = await api.patch(`/admin-branches/${branchId}/deactivate`, {
    reason,
  });
  return response.data;
};

const getBranchAnalytics = async (branchId, dateRange) => {
  const response = await api.get(`/admin-branches/${branchId}/analytics`, {
    params: dateRange,
  });
  return response.data;
};

const getBranchHierarchy = async () => {
  const response = await api.get(`/admin-branches/hierarchy`);
  return response.data;
};

// Store Management
const createStore = async (branchId, storeData) => {
  const response = await api.post(
    `/admin-branches/${branchId}/stores`,
    storeData
  );
  return response.data;
};

const getBranchStores = async (branchId, queryParams) => {
  const response = await api.get(`/admin-branches/${branchId}/stores`, {
    params: queryParams,
  });
  return response.data;
};

const updateStore = async (branchId, storeId, storeData) => {
  const response = await api.put(
    `/admin-branches/${branchId}/stores/${storeId}`,
    storeData
  );
  return response.data;
};

const deleteStore = async (branchId, storeId) => {
  const response = await api.delete(
    `/admin-branches/${branchId}/stores/${storeId}`
  );
  return response.data;
};

const transferStore = async (branchId, storeId, newBranchId) => {
  const response = await api.post(
    `/admin-branches/${branchId}/stores/${storeId}/transfer`,
    {
      newBranchId,
    }
  );
  return response.data;
};

const getStoreMetrics = async (branchId, storeId, dateRange) => {
  const response = await api.get(
    `/admin-branches/${branchId}/stores/${storeId}/metrics`,
    {
      params: dateRange,
    }
  );
  return response.data;
};

// Bulk Operations
const bulkUpdateBranches = async (branchIds, updateData) => {
  const response = await api.put(`/admin-branches/bulk-update`, {
    branchIds,
    updateData,
  });
  return response.data;
};

const bulkDeactivateBranches = async (branchIds, reason) => {
  const response = await api.patch(`/admin-branches/bulk-deactivate`, {
    branchIds,
    reason,
  });
  return response.data;
};

const configureHybridPolicy = async (branchId, hybridConfig) => {
  const response = await api.put(`/admin-branches/${branchId}/hybrid-config`, {
    hybridConfig,
  });
  return response.data;
};

const validateInheritance = async (branchId, targetBranchId) => {
  const response = await api.post(`/admin-branches/validate-inheritance`, {
    branchId,
    targetBranchId,
  });
  return response.data;
};

const getInheritanceSources = async (branchId) => {
  const response = await api.get(
    `/admin-branches/${branchId}/inheritance-sources`
  );
  return response.data;
};

const getInheritanceChain = async (branchId) => {
  const response = await api.get(
    `/admin-branches/${branchId}/inheritance-chain`
  );
  return response.data;
};

const getDependentBranches = async (branchId) => {
  const response = await api.get(`/admin-branches/${branchId}/dependents`);
  return response.data;
};

const updateBranchOwner = async (branchId, ownerId) => {
  const response = await api.post(`/admin-branches/update-owner`, {
    branchId,
    ownerId,
  });
  return response.data;
};

const updateAdminPrimaryBranch = async (adminId, branchCode) => {
  const response = await api.post(`/admin-branches/admin-primary-branch`, {
    adminId,
    branchCode,
  });
  return response.data;
};

const updateAdminSecondaryBranches = async (adminId, branchCodes) => {
  const response = await api.post(`/admin-branches/admin-secondary-branches`, {
    adminId,
    branchCodes,
  });
  return response.data;
};

// Exchange Charges Management
const getExchangeCharges = async (branchId) => {
  const response = await api.get(
    `/admin-branches/${branchId}/exchange-charges`
  );
  return response.data;
};

const updateExchangeCharges = async (branchId, exchangeCharges) => {
  const response = await api.put(
    `/admin-branches/${branchId}/exchange-charges`,
    exchangeCharges
  );
  return response.data;
};

const getDefaultExchangeCharges = async () => {
  const response = await api.get(`/admin-branches/exchange-charges/defaults`);
  return response.data;
};

const compareExchangeCharges = async (branchCodes) => {
  const response = await api.get(`/admin-branches/exchange-charges/compare`, {
    params: { branchCodes: branchCodes.join(",") },
  });
  return response.data;
};

const copyExchangeCharges = async (sourceBranchId, targetBranchIds) => {
  const response = await api.post(`/admin-branches/exchange-charges/copy`, {
    sourceBranchId,
    targetBranchIds,
  });
  return response.data;
};

const validateExchangeCharges = async (exchangeCharges) => {
  const response = await api.post(
    `/admin-branches/exchange-charges/validate`,
    exchangeCharges
  );
  return response.data;
};

const resetExchangeCharges = async (branchId) => {
  const response = await api.post(
    `/admin-branches/${branchId}/exchange-charges/reset`
  );
  return response.data;
};

const branchService = {
  // Branch operations
  createBranch,
  getBranches,
  getBranch,
  getBranchByCode,
  updateBranch,
  configureRateMode,
  setMainBranch,
  copyConfiguration,
  deactivateBranch,
  getBranchAnalytics,
  getBranchHierarchy,
  validateInheritance,
  getInheritanceSources,
  getInheritanceChain,
  getDependentBranches,
  configureHybridPolicy,
  // Store operations
  createStore,
  getBranchStores,
  updateStore,
  deleteStore,
  transferStore,
  getStoreMetrics,
  // Bulk operations
  bulkUpdateBranches,
  bulkDeactivateBranches,
  configureHybridPolicy,

  updateBranchOwner,
  updateAdminPrimaryBranch,
  updateAdminSecondaryBranches,

  // Exchange Charges operations
  getExchangeCharges,
  updateExchangeCharges,
  getDefaultExchangeCharges,
  compareExchangeCharges,
  copyExchangeCharges,
  validateExchangeCharges,
  resetExchangeCharges,
};

export default branchService;
